import React, { useState, useEffect } from 'react';
import { Tag, Printer, RotateCcw, XCircle, CheckCircle, AlertCircle } from 'lucide-react';
import labelService from '../services/labelService';
import toast from 'react-hot-toast';
import LabelPreview from './LabelPreview';
import UnifiedLabelPrintModal from './UnifiedLabelPrintModal';
import ConfirmModal from './ConfirmModal';

const LabelManager = ({ planId, positions = [], planCode, projectId, isOpen, onClose }) => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmCallback, setConfirmCallback] = useState(null);

  useEffect(() => {
    if (isOpen && planId) {
      loadLabels();
    }
  }, [isOpen, planId]);

  const loadLabels = async () => {
    try {
      setLoading(true);
      const response = await labelService.getLabelsByPlan(planId);
      if (response.success && Array.isArray(response.data)) {
        setLabels(response.data);
      } else {
        setLabels([]);
      }
    } catch (error) {
      console.error('Erro ao carregar etiquetas:', error);
      toast.error('Erro ao carregar etiquetas');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLabels = async () => {
    if (positions.length === 0) {
      toast.error('Não há posições para gerar etiquetas');
      return;
    }

    try {
      setLoading(true);
      const positionIds = positions.map(p => p.id);
      const response = await labelService.generateLabels(planId, positionIds, planCode, projectId);

      if (response.success) {
        toast.success(`${response.count} etiqueta(s) gerada(s) com sucesso`);
        loadLabels();
      } else {
        toast.error('Erro ao gerar etiquetas');
      }
    } catch (error) {
      console.error('Erro ao gerar etiquetas:', error);
      toast.error('Erro ao gerar etiquetas');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (labelsToPrint) => {
    try {
      for (const label of labelsToPrint) {
        if (label.status !== 'INVALIDATED') {
          await labelService.printLabel(label.id);
        }
      }
      toast.success('Etiqueta(s) enviada(s) para impressão');
      loadLabels();
      setShowPrintModal(false);
      setSelectedLabels([]);
    } catch (error) {
      console.error('Erro ao imprimir:', error);
      toast.error('Erro ao imprimir etiquetas');
      throw error;
    }
  };

  const handleInvalidate = (label) => {
    setConfirmMessage(`Tem certeza que deseja invalidar a etiqueta ${label.label_code}?`);
    setConfirmCallback(() => async () => {
      try {
        await labelService.invalidateLabel(label.id, 'Invalidada pelo usuário');
        toast.success('Etiqueta invalidada com sucesso');
        loadLabels();
      } catch (error) {
        console.error('Erro ao invalidar etiqueta:', error);
        toast.error('Erro ao invalidar etiqueta');
      }
    });
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    if (confirmCallback) {
      confirmCallback();
    }
    setShowConfirmModal(false);
    setConfirmCallback(null);
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    setConfirmCallback(null);
  };

  const handleReprint = async (label) => {
    try {
      await labelService.reprintLabel(label.id);
      toast.success('Etiqueta reenviada para impressão');
      loadLabels();
    } catch (error) {
      console.error('Erro ao reimprimir:', error);
      toast.error('Erro ao reimprimir etiqueta');
    }
  };

  const handleSelectLabel = (label) => {
    if (label.status === 'INVALIDATED') return;
    setSelectedLabels(prev => {
      const isSelected = prev.find(l => l.id === label.id);
      if (isSelected) {
        return prev.filter(l => l.id !== label.id);
      } else {
        return [...prev, label];
      }
    });
  };

  const handleSelectAll = () => {
    const validLabels = labels.filter(l => l.status !== 'INVALIDATED');
    if (selectedLabels.length === validLabels.length) {
      setSelectedLabels([]);
    } else {
      setSelectedLabels(validLabels);
    }
  };

  if (!isOpen) return null;

  const stats = {
    total: labels.length,
    pending: labels.filter(l => l.status === 'PENDING').length,
    printed: labels.filter(l => l.status === 'PRINTED').length,
    invalidated: labels.filter(l => l.status === 'INVALIDATED').length
  };

  return (
    <div className="fixed inset-0 z-40 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Tag className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Gerenciar Etiquetas</h2>
              <p className="text-sm text-gray-500">Plano: {planCode}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <span className="text-sm"><strong>{stats.printed}</strong> impressas</span>
              </div>
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <span className="text-sm"><strong>{stats.pending}</strong> pendentes</span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-600" />
                <span className="text-sm"><strong>{stats.invalidated}</strong> invalidadas</span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleGenerateLabels}
                disabled={loading || positions.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Tag className="w-4 h-4" />
                Gerar Etiquetas
              </button>
              <button
                onClick={() => setShowPrintModal(true)}
                disabled={selectedLabels.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                <Printer className="w-4 h-4" />
                Imprimir ({selectedLabels.length})
              </button>
            </div>
          </div>

          {positions.length === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                Adicione posições na chapa primeiro para gerar etiquetas.
              </p>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Carregando...</span>
            </div>
          ) : labels.length === 0 ? (
            <div className="text-center py-12">
              <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma etiqueta gerada</h3>
              <p className="text-gray-500">Clique em "Gerar Etiquetas" para criar as etiquetas das peças.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {labels.map((label) => (
                <div
                  key={label.id}
                  className={`relative cursor-pointer transition-all ${
                    label.status === 'INVALIDATED' ? 'opacity-50' : ''
                  }`}
                  onClick={() => handleSelectLabel(label)}
                >
                  {selectedLabels.find(l => l.id === label.id) && (
                    <div className="absolute top-2 right-2 z-10 bg-blue-600 text-white rounded-full p-1">
                      <CheckCircle className="w-3 h-3" />
                    </div>
                  )}
                  <LabelPreview label={label} />
                  {label.status !== 'INVALIDATED' && (
                    <div className="flex gap-1 mt-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleReprint(label); }}
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                      >
                        <RotateCcw className="w-3 h-3" />
                        Reimprimir
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleInvalidate(label); }}
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded transition-colors"
                      >
                        <XCircle className="w-3 h-3" />
                        Invalidar
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <UnifiedLabelPrintModal
        labels={selectedLabels.length > 0 ? selectedLabels : labels}
        isOpen={showPrintModal}
        onClose={() => {
          setShowPrintModal(false);
          setSelectedLabels([]);
        }}
        title="Imprimir Etiquetas do Plano de Corte"
        sourceType="CUTTING_PLAN"
      />

      {showConfirmModal && (
        <ConfirmModal
          message={confirmMessage}
          onConfirm={handleConfirm}
          onCancel={handleCancelConfirm}
        />
      )}
    </div>
  );
};

export default LabelManager;
